// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// Copyright 2026 Amantus Machina GmbH

'use client';

import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';
import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useAuth } from '@/hooks/useAuth';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { isBrowserLocalhost } from '@/lib/utils/runtime';

type ProductlaneQueueMethod = 'set' | 'open' | 'close' | 'toggle' | 'on' | 'off' | 'init' | 'enable' | 'disable';

type ProductlaneBootstrapAPI = ProductlaneAPI & {
  on?: () => void;
  off?: () => void;
  toggle?: () => void;
  queue: Partial<Record<ProductlaneQueueMethod, { args: unknown[] }>>;
};

type ProductlaneWindow = Window &
  typeof globalThis & {
    Productlane?: ProductlaneAPI & {
      on?: () => void;
      off?: () => void;
      toggle?: () => void;
      queue?: Record<string, unknown>;
    };
  };

const getProductlane = (): ProductlaneAPI | undefined => {
  const browserWindow: ProductlaneWindow | null = getBrowserWindow();
  return browserWindow?.Productlane;
};

type ProductlaneAPI = {
  enable?: () => void;
  disable?: () => void;
  open?: () => void;
  close?: () => void;
  init?: (config: {
    widgetKey: string;
    mode: 'light' | 'dark';
    user?: { email: string; name?: string; id: string };
  }) => void;
  set?: (options: { mode: 'light' | 'dark' }) => void;
};

export function ProductlaneWidget() {
  const { user, isLoading } = useAuth();
  const { resolvedTheme } = useTheme();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [shouldLoadWidget, setShouldLoadWidget] = useState(false);
  const widgetInitializedReference = useRef(false);
  const openRequestedReference = useRef(false);
  const pathname = usePathname();
  const hideForPath = pathname?.startsWith('/explore') ?? false;
  const isMobile = useMediaQuery('(max-width: 768px)');
  // Skip rendering on localhost; keep hooks order intact
  const isLocalhost = isBrowserLocalhost();
  const sessionUser = user ?? null;
  const widgetMode = useMemo<'light' | 'dark'>(() => (resolvedTheme === 'dark' ? 'dark' : 'light'), [resolvedTheme]);

  const widgetUnavailable = isMobile || hideForPath || isLocalhost;
  const authBlocked = isLoading || !sessionUser;

  const handleOpenRequest = useEffectEvent(() => {
    if (widgetUnavailable) {
      return;
    }

    openRequestedReference.current = true;
    setShouldLoadWidget(true);

    if (!authBlocked) {
      const productlane = getProductlane();
      productlane?.enable?.();
      productlane?.open?.();
    }
  });

  useEffect(() => {
    const browserWindow = getBrowserWindow();
    if (!browserWindow) {
      return;
    }

    const handleEvent = () => {
      handleOpenRequest();
    };

    browserWindow.addEventListener('sweetistics:open-productlane', handleEvent);
    return () => {
      browserWindow.removeEventListener('sweetistics:open-productlane', handleEvent);
    };
  }, []);

  useEffect(() => {
    if (widgetUnavailable) {
      setShouldLoadWidget(false);
      openRequestedReference.current = false;
      const productlane = getProductlane();
      productlane?.close?.();
      productlane?.disable?.();
      return;
    }

    getProductlane()?.enable?.();
  }, [widgetUnavailable]);

  useEffect(() => {
    if (widgetUnavailable) {
      return;
    }

    if (authBlocked) {
      if (!openRequestedReference.current) {
        setShouldLoadWidget(false);
      }
      const productlane = getProductlane();
      productlane?.close?.();
      productlane?.disable?.();
    } else if (openRequestedReference.current && !shouldLoadWidget) {
      setShouldLoadWidget(true);
      const productlane = getProductlane();
      productlane?.enable?.();
      productlane?.open?.();
    }
  }, [authBlocked, shouldLoadWidget, widgetUnavailable]);

  useEffect(() => {
    if (widgetUnavailable || !shouldLoadWidget) {
      return;
    }

    const browserWindow: ProductlaneWindow | null = getBrowserWindow();
    const browserDocument = browserWindow?.document;
    if (!(browserWindow && browserDocument)) {
      return;
    }

    if (!browserWindow.Productlane) {
      const productlane: ProductlaneBootstrapAPI = { queue: {} };
      browserWindow.Productlane = productlane;
      const methods = [
        'set',
        'open',
        'close',
        'toggle',
        'on',
        'off',
        'init',
        'enable',
        'disable',
      ] as const satisfies readonly ProductlaneQueueMethod[];
      for (const method of methods) {
        if (typeof productlane[method] !== 'function') {
          productlane[method] = (...args: unknown[]) => {
            productlane.queue[method] = { args };
          };
        }
      }
    }

    const existingScript = browserDocument.querySelector<HTMLScriptElement>('#productlane-widget-script');
    const handleLoad = () => {
      setScriptLoaded(true);
    };

    if (existingScript instanceof HTMLScriptElement) {
      if (existingScript.dataset['loaded'] === 'true') {
        setScriptLoaded(true);
      } else {
        existingScript.addEventListener('load', handleLoad, { once: true });
      }
      return () => {
        existingScript.removeEventListener('load', handleLoad);
      };
    }

    const scriptElement = browserDocument.createElement('script');
    scriptElement.id = 'productlane-widget-script';
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.crossOrigin = 'anonymous';
    scriptElement.src = 'https://widget.productlane.com/latest.productlane-widget.min.js';
    scriptElement.addEventListener(
      'load',
      () => {
        scriptElement.dataset['loaded'] = 'true';
        handleLoad();
      },
      { once: true }
    );
    browserDocument.head.append(scriptElement);

    return () => {
      scriptElement.removeEventListener('load', handleLoad);
    };
  }, [shouldLoadWidget, widgetUnavailable]);

  // Initialize widget when script is loaded and dependencies are ready
  useEffect(() => {
    // Skip initialization when the widget should not render or auth is missing
    if (widgetUnavailable || !shouldLoadWidget || authBlocked) return;

    const productlane = getProductlane();
    if (scriptLoaded && sessionUser && !widgetInitializedReference.current && productlane?.init) {
      // Initialize Productlane with user data and theme
      const userPayload = sessionUser.email
        ? {
            email: sessionUser.email,
            id: sessionUser.id,
            ...(sessionUser.name ? { name: sessionUser.name } : {}),
          }
        : null;
      productlane.init({
        widgetKey: '1fb53748-362f-4c1f-877f-db69eb1d10e9',
        mode: widgetMode,
        ...(userPayload ? { user: userPayload } : {}),
      });

      widgetInitializedReference.current = true;

      if (openRequestedReference.current) {
        productlane?.enable?.();
        productlane?.open?.();
      }
    }
  }, [authBlocked, scriptLoaded, sessionUser, shouldLoadWidget, widgetMode, widgetUnavailable]);

  // Sync theme changes (including OS changes) with Productlane widget
  useEffect(() => {
    // Skip on localhost
    if (widgetUnavailable || !shouldLoadWidget) return;

    if (widgetInitializedReference.current) {
      getProductlane()?.set?.({ mode: widgetMode });
    }
  }, [widgetMode, shouldLoadWidget, widgetUnavailable]);

  // Only render for authenticated users and never on localhost
  if (hideForPath || isLocalhost || isLoading || !sessionUser || isMobile) {
    return null;
  }

  if (!shouldLoadWidget) {
    return null;
  }

  return null;
}
